import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import { buildLoginUrl } from "@/lib/redirect";

interface AuthSheetContextValue {
  openLogin: () => void;
}

const AuthSheetContext = createContext<AuthSheetContextValue | null>(null);

export function AuthSheetProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const openLogin = useCallback(() => setOpen(true), []);

  const handleLogin = () => {
    setOpen(false);
    navigate(buildLoginUrl());
  };

  return (
    <AuthSheetContext.Provider value={{ openLogin }}>
      {children}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="text-center pb-4">
            <div className="flex justify-center pb-2">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <Heart className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
            <SheetTitle className="text-lg">Spara för senare</SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">
              Logga in för att spara dina favoriter och återvända till dem.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-3 pt-2 pb-4">
            <Button onClick={handleLogin} className="w-full">
              Logga in
            </Button>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="w-full text-muted-foreground"
            >
              Inte nu
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </AuthSheetContext.Provider>
  );
}

/**
 * Safe hook: returns null if AuthSheetProvider is not mounted.
 */
export function useAuthSheet(): AuthSheetContextValue | null {
  return useContext(AuthSheetContext);
}
